import { computed, watch } from 'vue'
import { prop, zipObj } from 'ramda'
import { parse } from 'parser-mantester'
import { distinct, duplicate, nonNull, sum } from '../../shared/fns'
import { combinationId } from './setup-utils'
import { loadedTestCases, parsedTestCases, selectedCombinationIds, testCasesSource } from './setup-state'

const preprocessParsedTestCases = data =>
  data
    .testCases
    .map(testCase => ({
      ...testCase,
      status: 'ready',
      code: testCase.code || testCase.name.replace(/\s/g, '-'),
    }))

watch(testCasesSource, () => {
  parsedTestCases.value = parse(testCasesSource.value)
  loadedTestCases.value = preprocessParsedTestCases(parsedTestCases.value)
})

export const parsedTestCasesErrors = computed(() => {
  const noTestCaseCodeAndNameCount = parsedTestCases.value?.testCases
    .filter(({ code, name }) => !code && !name)
    .length
  const noTestCaseCode = parsedTestCases.value?.testCases
    .filter(({ code }) => !code)
    .map(({ name }) => name)
  const duplicateTestCaseCode = loadedTestCases.value
    .map(({ code }) => code)
    .filter(duplicate)
    .filter(distinct)
    .filter(code => !!code)
  const noTestCaseCategory = loadedTestCases.value
    .filter(({ category }) => !category)
    .map(({ code }) => code)
  const noTestCaseGroup = loadedTestCases.value
    .filter(({ groups }) => !groups.length)
    .map(({ code }) => code)
  const noTestCaseName = loadedTestCases.value
    .filter(({ name }) => !name)
    .map(({ code }) => code)
  const noTestSteps = loadedTestCases.value
    .filter(({ steps }) => !steps.length)
    .map(({ code, name }) => ({ code, name }))

  const hasError = noTestCaseCodeAndNameCount || noTestCaseCode?.length || duplicateTestCaseCode?.length ||
    noTestCaseCategory?.length || noTestCaseGroup?.length || noTestCaseName?.length || noTestSteps?.length

  return {
    hasError,
    noTestCaseCodeAndNameCount,
    noTestCaseCode,
    duplicateTestCaseCode,
    noTestCaseCategory,
    noTestCaseGroup,
    noTestCaseName,
    noTestSteps,
  }
})

export const selectedTestCases = computed(() =>
  loadedTestCases.value
    ?.filter(
      ({ category, groups }) =>
        selectedCombinationIds.value?.includes(combinationId({ category, groups }))
    ) ?? []
)

export const combinations = computed(() =>
  loadedTestCases.value
    .map(({ category, groups }) => ({
      category,
      groups: groups.sort(),
      id: combinationId({ category, groups }),
    }))
    .map((item, _, array) => ({
      ...item,
      uniqueId: array.findIndex(({ id }) => id === item.id),
    }))
    .map((item, _, array) => ({
      ...item,
      count: array.filter(({ uniqueId }) => uniqueId === item.uniqueId).length,
      stepCount: loadedTestCases.value
        .filter(({ category, groups }) => item.id === combinationId({ category, groups }))
        .flatMap(({ steps }) => steps)
        .length,
    }))
    .filter((item, index, array) => array.findIndex(({ id }) => id === item.id) === index)
    .sort((a, b) => a.id < b.id ? -1 : b.id > a.id ? 1 : 0)
)

const findCombinationById = id =>
  combinations.value
    .find(combination => combination.id === id)

const categoryCombinations = name =>
  combinations.value
    .filter(({ category }) => category === name)

const groupCombinations = name =>
  combinations.value
    .filter(({ groups }) => groups.includes(name))

const getCount = (type, selected, combinations) =>
  sum(...combinations
    .filter(({ id }) => selected === selectedCombinationIds.value.includes(id))
    .map(item => item[type])
  )

const categoryCount = (selected, name) =>
  getCount('count', selected, categoryCombinations(name))

const groupCount = (selected, name) =>
  getCount('count', selected, groupCombinations(name))

export const allTestCases = computed(() => ({
  name: 'All test cases',
  selectedCount: selectedTestCasesCount.value,
  unselectedCount: unselectedTestCasesCount.value,
}))

export const categories = computed(() =>
  combinations.value
    .map(({ category }) => category)
    .filter(nonNull)
    .filter(distinct)
    .map(name => ({
      name,
      selectedCount: categoryCount(true, name),
      unselectedCount: categoryCount(false, name),
    }))
)

const groupNames = computed(() =>
  combinations.value
    .flatMap(({ groups }) => groups)
    .filter(distinct)
)

export const groups = computed(() =>
  groupNames.value
    .map(name => ({
      name,
      selectedCount: groupCount(true, name),
      unselectedCount: groupCount(false, name),
    }))
)

const groupTestCaseCodes = computed(() =>
  zipObj(groupNames.value,
    groupNames.value
      .map(name =>
        loadedTestCases.value
          .filter(({ groups }) => groups.includes(name))
          .map(({ code }) => code)
      )
  )
)

const groupParents = computed(() =>
  zipObj(groupNames.value,
    groupNames.value
      .map(name =>
        groupNames.value
          .filter(other => other !== name)
          .filter(other =>
            groupTestCaseCodes.value[name]
              .every(code => groupTestCaseCodes.value[other].includes(code)))
          .filter(other =>
            groupTestCaseCodes.value[name].length < groupTestCaseCodes.value[other].length ||
            groupNames.value.indexOf(other) < groupNames.value.indexOf(name)
          )
          .map(name => ({ name, size: groupTestCaseCodes.value[name].length }))
          .sort((a, b) => a.size - b.size)[0]?.name
      )
  )
)

export const rootGroups = computed(() =>
  groupNames.value
    .filter(name => !groupParents.value[name])
    .map(name => ({ name, size: groupTestCaseCodes.value[name].length }))
    .sort((a, b) => b.size - a.size)
    .map(({ name }) => name)
)

export const groupChildren = computed(() =>
  zipObj(groupNames.value,
    groupNames.value
      .map(name =>
        groupNames.value
          .filter(other => groupParents.value[other] === name)
      )
  )
)

export const isSelected = id => selectedCombinationIds.value.includes(id)

export const toggleAll = () => {
  if (unselectedTestCasesCount.value) { // select
    selectedCombinationIds.value = combinations.value.map(prop('id'))
  } else { // deselect
    selectedCombinationIds.value = []
  }
}

export const toggleCombination = id => {
  if (isSelected(id)) { // deselect
    selectedCombinationIds.value = selectedCombinationIds.value
      .filter(selectedId => selectedId !== id)
  } else { // select
    selectedCombinationIds.value = selectedCombinationIds.value
      .concat([id])
  }
}

export const toggleCategory = name => {
  const category = categories.value
    .find(category => category.name === name)
  if (category.unselectedCount) { // select
    selectedCombinationIds.value = selectedCombinationIds.value
      .concat(categoryCombinations(name).map(prop('id')))
      .filter(distinct)
  } else { // deselect
    selectedCombinationIds.value = selectedCombinationIds.value
      .filter(selectedId => findCombinationById(selectedId)?.category !== name)
  }
}

export const toggleGroup = name => {
  const group = groups.value
    .find(group => group.name === name)
  if (group.unselectedCount) { // select
    selectedCombinationIds.value = selectedCombinationIds.value
      .concat(groupCombinations(name).map(prop('id')))
      .filter(distinct)
  } else { // deselect
    selectedCombinationIds.value = selectedCombinationIds.value
      .filter(selectedId => !findCombinationById(selectedId)?.groups.includes(name))
  }
}

export const selectedTestCasesCount = computed(() => getCount('count', true, combinations.value))
export const unselectedTestCasesCount = computed(() => getCount('count', false, combinations.value))
export const selectedStepsCount = computed(() => getCount('stepCount', true, combinations.value))
